/** @file gizmoModel: where the gizmo sits and which way its axes point for a selection (pure). */
import { quatRotateVec3 } from '@zilo/pose3d-math/quat/quatRotateVec3';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import type { PivotMode, TransformSpace } from '../../state/editorTypes';
import { GIZMO_AXES } from './gizmoAxes';
import { poseNode } from './poseNode';

/** World origin (entity origin + pose position) and the three handle axes (unit, world, X east / Y north / Z up). */
export type GizmoModel = { origin: Vec3; axes: [Vec3, Vec3, Vec3]; active: number };

/** Where a single-joint gizmo sits: on the node itself, or at the base of its bone (the parent end). */
export type GizmoAt = 'node' | 'base';

/**
 * `selection` holds pose indices; the last one is active. `local`: the active node's world rotation applied to
 * `GIZMO_AXES`; `world`: `GIZMO_AXES`. `pivot`: at the active joint; `center`: the mean of the selected joints.
 * Null for an empty selection.
 */
export function gizmoModel(selection: readonly number[], pose: PoseBuffer, space: TransformSpace, pivot: PivotMode, entityPos: Vec3 = [0, 0, 0], at: GizmoAt = 'node'): GizmoModel | null {
  const active = selection.at(-1);
  if (active === undefined) return null;
  const node = poseNode(pose, active);
  // `base`: the bone turns about its base (a key turns the bone from the parent to the node), so the gizmo sits there
  const own = at === 'base' && node.parent >= 0 ? node.origin : node.pos;
  const points = pivot === 'pivot' ? [own] : selection.map((i) => poseNode(pose, i).pos);
  const sum = points.reduce<Vec3>((s, p) => [s[0] + p[0], s[1] + p[1], s[2] + p[2]], [0, 0, 0]);
  const n = points.length;
  const origin: Vec3 = [entityPos[0] + sum[0] / n, entityPos[1] + sum[1] / n, entityPos[2] + sum[2] / n];
  const turn = (a: Vec3): Vec3 => (space === 'local' ? quatRotateVec3(node.q, a) : [a[0], a[1], a[2]]);
  return { origin, axes: [turn(GIZMO_AXES[0]), turn(GIZMO_AXES[1]), turn(GIZMO_AXES[2])], active };
}
